import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Microscope, Plus, Target, BookOpen, Lightbulb } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ProjectResearchCard } from './ProjectResearchCard';

interface ProjectResearchGridProps {
  research: any[];
  isLoading: boolean;
  onResearchClick: (attachmentId: string) => void;
  onToggleFavorite: (researchId: string) => Promise<void>;
  onCreateResearch?: () => void;
  searchQuery?: string;
}

export const ProjectResearchGrid: React.FC<ProjectResearchGridProps> = ({
  research,
  isLoading,
  onResearchClick,
  onToggleFavorite,
  onCreateResearch,
  searchQuery,
}) => {
  const handleCreateResearch = () => {
    if (onCreateResearch) {
      onCreateResearch();
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 2xl:grid-cols-2 gap-2 md:gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="animate-pulse">
            <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl border border-white/20 dark:border-gray-700/30 rounded-2xl p-6 h-64">
              <div className="flex items-center justify-between mb-4">
                <div className="h-6 bg-green-200/50 dark:bg-green-800/30 rounded-full w-24"></div>
              </div>
              <div className="space-y-3">
                <div className="h-6 bg-gray-200/50 dark:bg-gray-700/30 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200/50 dark:bg-gray-700/30 rounded w-full"></div>
                <div className="h-4 bg-gray-200/50 dark:bg-gray-700/30 rounded w-2/3"></div>
              </div>
              <div className="mt-auto pt-4 flex justify-between items-center">
                <div className="h-8 bg-gray-200/50 dark:bg-gray-700/30 rounded w-20"></div>
                <div className="h-4 bg-gray-200/50 dark:bg-gray-700/30 rounded w-16"></div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Empty state
  if (research.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center max-w-2xl mx-auto">
        {/* Animated illustration with floating elements */}
        <div className="relative mb-8">
          {/* Background floating circles */}
          <div className="absolute inset-0 -m-8">
            <motion.div
              className="absolute top-0 left-0 w-4 h-4 bg-purple-400/30 rounded-full"
              animate={{ 
                y: [0, -10, 0],
                opacity: [0.3, 0.6, 0.3]
              }}
              transition={{ 
                repeat: Infinity, 
                duration: 3,
                ease: "easeInOut"
              }}
            />
            <motion.div
              className="absolute top-4 right-2 w-3 h-3 bg-blue-400/40 rounded-full"
              animate={{ 
                y: [0, -8, 0],
                opacity: [0.4, 0.7, 0.4]
              }}
              transition={{ 
                repeat: Infinity, 
                duration: 2.5,
                delay: 0.5,
                ease: "easeInOut"
              }}
            />
            <motion.div
              className="absolute bottom-2 left-4 w-2 h-2 bg-green-400/50 rounded-full"
              animate={{ 
                y: [0, -6, 0],
                opacity: [0.5, 0.8, 0.5]
              }}
              transition={{ 
                repeat: Infinity, 
                duration: 2.8,
                delay: 1,
                ease: "easeInOut"
              }}
            />
          </div>
          
          {/* Main icon with gradient background */}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-blue-500/20 dark:from-purple-400/10 dark:to-blue-400/10 rounded-full blur-xl"></div>
            <motion.div
              className="relative z-10 bg-gradient-to-br from-white/60 to-white/30 dark:from-white/10 dark:to-white/5 backdrop-blur-sm border border-white/20 dark:border-white/10 rounded-full p-6 shadow-lg"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              whileHover={{ scale: 1.05 }}
            >
              <motion.div
                animate={{ 
                  rotate: [0, 5, -5, 0],
                }}
                transition={{ 
                  repeat: Infinity, 
                  duration: 4,
                  ease: "easeInOut"
                }}
              >
                <Microscope className="h-16 w-16 text-green-500 dark:text-green-400" />
              </motion.div>
            </motion.div>
          </div>
        </div>
        
        {/* Compelling headline */}
        <motion.h3 
          className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-green-500 to-emerald-400 dark:from-green-400 dark:to-emerald-300 mb-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {searchQuery ? 'No Research Found' : 'Ready to Discover Insights?'}
        </motion.h3>
        
        {/* Benefit-focused description */}
        <motion.p 
          className="text-gray-600 dark:text-gray-300 mb-8 max-w-md leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {searchQuery 
            ? `No research matches "${searchQuery}". Try a different search term or generate new research.`
            : 'Generate AI-powered research to enhance your project. Discover data, insights, and knowledge that will elevate your content.'
          }
        </motion.p>

        {/* Benefits grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 max-w-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <div className="flex flex-col items-center text-center p-3">
            <Target className="h-8 w-8 text-green-500 dark:text-green-400 mb-2" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Stay Informed</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Get accurate data</p>
          </div>
          <div className="flex flex-col items-center text-center p-3">
            <BookOpen className="h-8 w-8 text-green-500 dark:text-green-400 mb-2" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Build Knowledge</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Deepen understanding</p>
          </div>
          <div className="flex flex-col items-center text-center p-3">
            <Lightbulb className="h-8 w-8 text-green-500 dark:text-green-400 mb-2" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Generate Ideas</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Spark innovation</p>
          </div>
        </motion.div>
        
        {/* Call-to-action button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <Button 
            onClick={handleCreateResearch}
            className="relative group overflow-hidden bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-4 px-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
          >
            {/* Shimmer effect */}
            <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12" />
            
            {/* Button content */}
            <div className="relative flex items-center justify-center gap-3">
              <Plus className="w-5 h-5" />
              <span>Generate Your First Research</span>
            </div>
          </Button>
        </motion.div>

        {/* Subtle hint */}
        <motion.p 
          className="text-xs text-gray-400 dark:text-gray-500 mt-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          {searchQuery ? 'Try adjusting your search terms' : 'Use AI suggestions to get started'}
        </motion.p>
      </div>
    );
  }

  // Research grid
  return (
    <motion.div 
      className="grid grid-cols-1 2xl:grid-cols-2 gap-2 md:gap-6"
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      style={{ 
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
        willChange: 'transform'
      }}
    >
      <AnimatePresence mode="popLayout">
        {research.map((researchItem, index) => (
          <ProjectResearchCard
            key={researchItem.id}
            research={researchItem}
            index={index}
            onResearchClick={onResearchClick}
            onToggleFavorite={onToggleFavorite}
          />
        ))}
      </AnimatePresence>
    </motion.div>
  );
};

export default ProjectResearchGrid; 